import styles from './TrendDelta.module.css';

export type TrendDirection = 'up' | 'down' | 'flat';

/**
 * App-wide "flat" band: a period-over-period move smaller than this many
 * points is reported as steady, matching Kai-API's trend classification.
 */
export const TREND_FLAT_BAND = 2;

/** Classify a delta with the shared +/- band. */
export function trendDirection(delta: number | null | undefined, band = TREND_FLAT_BAND): TrendDirection {
  if (delta == null || !Number.isFinite(delta)) return 'flat';
  if (delta >= band) return 'up';
  if (delta <= -band) return 'down';
  return 'flat';
}

interface TrendDeltaProps {
  /** Signed change. Null renders a muted dash. */
  delta: number | null | undefined;
  /** Override the computed direction (e.g. when the API already classified it). */
  direction?: TrendDirection;
  /** Appended after the number: "pts", "%", or nothing. */
  unit?: 'pts' | '%' | '';
  /** Decimal places for the magnitude. Default: 1 when |delta| < 10, else 0. */
  decimals?: number;
  /** Show the word "steady" instead of the number when flat. */
  steadyLabel?: boolean;
  /** Tone the glyph and number by direction (default) or leave inherit. */
  tone?: boolean;
  /** Size preset. */
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  title?: string;
}

/**
 * The one way the app shows a change over time.
 *
 * Arrow glyph (a crisp SVG, not a Unicode triangle that varies by font) +
 * unsigned magnitude + optional unit. The arrow IS the sign, so no "+"/"-"
 * is ever printed next to it. Flat renders a horizontal arrow and either the
 * magnitude or the word "steady". Colour follows direction via tokens.
 *
 * Use for every trend chip, pill, stat and label; keep sort-direction arrows
 * on column headers separate (those are controls, not data).
 */
export function TrendDelta({
  delta,
  direction,
  unit = '',
  decimals,
  steadyLabel = false,
  tone = true,
  size = 'md',
  className,
  title,
}: TrendDeltaProps) {
  const dir = direction ?? trendDirection(delta);
  const has = delta != null && Number.isFinite(delta);
  const mag = has ? Math.abs(delta as number) : 0;
  const places = decimals ?? (mag < 10 && !Number.isInteger(mag) ? 1 : 0);
  const num = has ? mag.toFixed(places) : null;

  const text = !has
    ? '–'
    : dir === 'flat' && steadyLabel
      ? 'steady'
      : `${num}${unit === '%' ? '%' : unit ? ` ${unit}` : ''}`;

  const cls = [
    styles.trend,
    styles[`size_${size}`],
    tone ? styles[`tone_${has ? dir : 'none'}`] : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  const label =
    title ??
    (!has
      ? 'No change data'
      : `${dir === 'up' ? 'Up' : dir === 'down' ? 'Down' : 'Steady'} ${num}${unit === '%' ? '%' : unit ? ` ${unit}` : ''}`);

  return (
    <span className={cls} title={label} aria-label={label}>
      {has && (
        <svg
          className={styles.glyph}
          viewBox="0 0 12 12"
          width="1em"
          height="1em"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          {dir === 'up' ? (
            <path d="M6 10V2M2.5 5.5 6 2l3.5 3.5" />
          ) : dir === 'down' ? (
            <path d="M6 2v8M2.5 6.5 6 10l3.5-3.5" />
          ) : (
            <path d="M2 6h8M7 3.5 9.5 6 7 8.5" />
          )}
        </svg>
      )}
      <span className={styles.value}>{text}</span>
    </span>
  );
}
